import { Suspense, lazy, useEffect, useMemo, useState } from "react";
import {
  useJson, getJson, nf, plural, ago, fmtDate, card, control, sectionLabel, Chip, ConfidenceBadge, KevBadge, IdBadge, entityPath, victimPath, listingPath, sanctionedPath, CATEGORY_BY_SLUG, Box, About, OnThisPage,
} from "./shared";
import { BarList } from "./Charts";
import { ReportCard } from "./Reports";
import { StixBar, StixExportLink, stixEntityUrl } from "./Stix";
import { PackageRows, PackagesState } from "./Packages";

// The missile map and its 1:50m world map load only when the Missiles tab opens.
const MissileMap = lazy(() => import("./MissileMap").then(m => ({ default: m.MissileMap })));

// Critical Infrastructure: #/kb/supply-chain, #/kb/defense-industry and #/kb/space (?incident=<id>
// picks one); ICS / SCADA is its own file, Ics.js. Geopolitics: #/kb/missiles and #/kb/export-controls,
// each with a page per listing, #/kb/listing/<id>.

// about: the rest of the introduction (what the page brings together), behind "About this page".
function Heading({ slug, ui, children, about }) {
  const { B, fs } = ui;
  const cat = CATEGORY_BY_SLUG[slug];
  return (
    <>
      <h2 style={{ fontSize: fs(22), fontWeight: 900, margin: "4px 0 6px", color: B.text }}>{cat.label}</h2>
      <div style={{ color: B.textMuted, fontSize: fs(13), margin: "0 0 16px", maxWidth: "110ch", lineHeight: 1.6 }}>{children || cat.blurb}{about && <About ui={ui} inline>{about}</About>}</div>
    </>
  );
}

const more = (ui, href, label) => <a href={href} style={{ fontSize: ui.fs(12), color: ui.B.accent, textTransform: "none", letterSpacing: 0, fontWeight: 600 }}>{label} →</a>;
const note = (ui, text) => <span style={{ textTransform: "none", letterSpacing: 0, fontWeight: 500 }}>{text}</span>;
const grid = (min) => ({ display: "grid", gridTemplateColumns: `repeat(auto-fit, minmax(min(100%, ${min}px), 1fr))`, gap: 14, alignItems: "start" });

// Country names in the reader's language, from the browser.
const regionNames = (() => { try { return new Intl.DisplayNames(["en"], { type: "region" }); } catch { return null; } })();
const countryName = (iso2) => { try { return regionNames?.of(iso2) || iso2; } catch { return iso2; } };

function EntityChips({ items, ui, max = 14 }) {
  const { getConf, fs, B } = ui;
  if (!items.length) return <span style={{ fontSize: fs(12), color: B.textDim }}>None in ATT&CK</span>;
  return (
    <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
      {items.slice(0, max).map(e => <a key={e.id} href={entityPath(e)} style={{ textDecoration: "none" }}><Chip ui={ui} color={getConf(e.type).color}>{e.name}</Chip></a>)}
      {items.length > max && <Chip ui={ui}>+{items.length - max} more</Chip>}
    </div>
  );
}

// ─── #/kb/supply-chain ───

export function KbSupplyChain({ ui }) {
  const { B, fs } = ui;
  const { data, error, loading } = useJson("/api/kb/defense/supply-chain");
  if (loading) return <><Heading slug="supply-chain" ui={ui} /><div style={{ color: B.textDim, fontSize: fs(13) }}>Loading…</div></>;
  if (error) return <><Heading slug="supply-chain" ui={ui} /><div style={{ color: "#dc2626", fontSize: fs(12) }}>{error}</div></>;
  const p = data.packages;
  const ecoRows = Object.entries(p.byEco).map(([k, v]) => ({ label: p.ecosystemNames[k] || k, value: v, href: `#/kb/packages?eco=${k}` }));
  const tagRows = Object.entries(p.byTag).map(([k, v]) => ({ label: k, value: v, href: `#/kb/packages?tag=${encodeURIComponent(k)}` }));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div>
        <Heading slug="supply-chain" ui={ui} about={`This page brings together the malicious packages reported in the last ${p.days} days, the ATT&CK techniques for supply-chain compromise and trusted relationships with the groups known for them, and the reports that cover it.`}>
          Attackers who can't break in directly go through what their targets install and trust: a malicious package slipped into npm or PyPI, a hijacked maintainer account that pushes a poisoned update, a compromised software vendor or IT provider.
        </Heading>
        <PackagesState st={data.state} ui={ui} />
        <OnThisPage ui={ui} />
      </div>

      <div style={grid(300)}>
        <Box title={`Malicious packages, last ${p.days} days`} ui={ui} right={more(ui, "#/kb/packages", "All")}>
          <div style={{ fontSize: fs(30), fontWeight: 900, color: B.text, lineHeight: 1.1, marginBottom: 8 }}>{nf(p.total)}</div>
          {ecoRows.length ? <BarList rows={ecoRows} ui={ui} unit="Packages" maxRows={6} /> : <div style={{ fontSize: fs(12), color: B.textDim }}>None loaded yet.</div>}
        </Box>
        <Box title="What they do" ui={ui} right={note(ui, "from each record's own words")}>
          {tagRows.length ? <BarList rows={tagRows} ui={ui} unit="Packages" maxRows={11} /> : <div style={{ fontSize: fs(12), color: B.textDim }}>Nothing yet.</div>}
        </Box>
        <Box title="Groups and malware named" ui={ui} right={note(ui, "inferred, Low")}>
          {p.named.length ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {p.named.map(n => (
                <div key={n.id} style={{ display: "flex", gap: 8, alignItems: "baseline", fontSize: fs(13) }}>
                  <a href={entityPath(n)} style={{ color: B.text, fontWeight: 700, textDecoration: "none" }}>{n.name}</a>
                  {n.attackId && <span style={{ fontSize: fs(11), color: B.textDim }}>{n.attackId}</span>}
                  <a href={`#/kb/packages?named=${encodeURIComponent(n.id)}`} style={{ marginLeft: "auto", fontSize: fs(12), color: B.accent, whiteSpace: "nowrap" }}>{nf(n.packages)} package{n.packages === 1 ? "" : "s"}</a>
                </div>
              ))}
              <div style={{ fontSize: fs(11), color: B.textDim, marginTop: 4, lineHeight: 1.5 }}>Found by name in the package records. A record may only say a package works like a known campaign, so these are leads, not attribution.</div>
            </div>
          ) : <div style={{ fontSize: fs(12), color: B.textDim }}>No ATT&CK group, campaign or malware family is named in the current records.</div>}
        </Box>
      </div>

      <Box title="Newest malicious packages" ui={ui} right={more(ui, "#/kb/packages", "Browse and filter")}>
        {p.recent.length ? <PackageRows items={p.recent} ui={ui} compact /> : <div style={{ fontSize: fs(12), color: B.textDim }}>Loading…</div>}
      </Box>
      {p.hijacked.length > 0 && (
        <Box title="Hijacked versions, with a group or malware named" jump="Hijacked versions" ui={ui} about="Only some versions are malicious, which usually means a real package whose maintainer account, token or build pipeline was taken over. These are the hardest to catch: the name is the one people already trust.">
          <PackageRows items={p.hijacked} ui={ui} compact />
        </Box>
      )}

      <div data-box="How supply chains are attacked" style={{ scrollMarginTop: 12 }}>
        <div style={{ ...sectionLabel(ui), margin: "6px 0 10px" }}>How supply chains are attacked (MITRE ATT&CK)</div>
        <div style={grid(360)}>
          {data.techniques.map(t => (
            <div key={t.id} style={{ ...card(ui), padding: "14px 18px", minWidth: 0 }}>
              <div style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap", marginBottom: 4 }}>
                <a href={entityPath(t)} style={{ fontSize: fs(14), fontWeight: 800, color: B.text, textDecoration: "none" }}>{t.name}</a>
                <span style={{ fontSize: fs(11), color: B.textDim }}>{t.attackId}</span>
              </div>
              {t.snippet && <div style={{ fontSize: fs(12), color: B.textMuted, lineHeight: 1.55, marginBottom: 10, display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{t.snippet}</div>}
              {(t.groups.length > 0 || !t.campaigns.length) && <><div style={{ fontSize: fs(11), color: B.textDim, marginBottom: 4 }}>Groups</div><EntityChips items={t.groups} ui={ui} /></>}
              {t.campaigns.length > 0 && <><div style={{ fontSize: fs(11), color: B.textDim, margin: "8px 0 4px" }}>Campaigns</div><EntityChips items={t.campaigns} ui={ui} /></>}
              {t.software.length > 0 && <><div style={{ fontSize: fs(11), color: B.textDim, margin: "8px 0 4px" }}>Malware and tools</div><EntityChips items={t.software} ui={ui} max={10} /></>}
            </div>
          ))}
        </div>
      </div>

      <Box title={`Reports on supply-chain attacks (${nf(data.reports.total)})`} jump="Reports" ui={ui} right={more(ui, "#/kb/reports?mentions=T1195", "Reports naming T1195")}>
        {data.reports.items.length
          ? <div style={grid(300)}>{data.reports.items.map(s => <ReportCard key={s.id} s={s} ui={ui} />)}</div>
          : <div style={{ fontSize: fs(12), color: B.textDim }}>None among the current reports. Reports count when they quote T1195 or T1199, talk about npm, PyPI or other registries, or are cited by a package record.</div>}
      </Box>
    </div>
  );
}

// ─── #/kb/defense-industry ───

const LABELS = ["Defense industry", "Aerospace", "Military", "Aviation"];

export function KbDefenseIndustry({ ui }) {
  const { B, fs, getConf, TypeIcon } = ui;
  const { data, error, loading } = useJson("/api/kb/defense/industry");
  const [label, setLabel] = useState("");
  if (loading) return <><Heading slug="defense-industry" ui={ui} /><div style={{ color: B.textDim, fontSize: fs(13) }}>Loading…</div></>;
  if (error) return <><Heading slug="defense-industry" ui={ui} /><div style={{ color: "#dc2626", fontSize: fs(12) }}>{error}</div></>;
  const byLabel = Object.fromEntries(LABELS.map(l => [l, data.actors.filter(a => a.labels.includes(l)).length]));
  const actors = label ? data.actors.filter(a => a.labels.includes(label)) : data.actors;
  const rw = data.ransomware;
  const ec = data.exportControls;
  const techRows = (rows) => rows.map(t => ({ label: `${t.attackId} ${t.name}`, value: t.count, href: entityPath(t) }));
  const pill = (on) => ({ ...ui.HP.navBtn, padding: "5px 11px", fontSize: fs(12), ...(on ? { color: B.text, borderColor: B.primary, background: B.primary + "14", fontWeight: 700 } : {}) });
  const cell = { padding: "9px 12px", borderTop: `1px solid ${B.border}`, fontSize: fs(12), verticalAlign: "top" };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div>
        <Heading slug="defense-industry" ui={ui} about="This page brings together the groups and campaigns MITRE ATT&CK says target them and how those groups get in, the defense and aerospace companies ransomware groups claim to have hit, reports on the defense industry, and the US export-control lists.">
          Defense contractors, aerospace companies and their suppliers hold what spies want (weapons designs, military contracts, export-controlled technology), and they're a way into the militaries they serve.
        </Heading>
        <StixBar apiKey="dib" ui={ui} />
        <OnThisPage ui={ui} />
      </div>

      <div style={grid(280)}>
        <Box title="Groups and campaigns that target it" jump="Targeted by" ui={ui}>
          <div style={{ fontSize: fs(30), fontWeight: 900, color: B.text, lineHeight: 1.1, marginBottom: 8 }}>{nf(data.actors.length)}</div>
          <BarList rows={LABELS.map(l => ({ label: l, value: byLabel[l] }))} ui={ui} unit="Groups and campaigns" keepOrder onSelect={(r) => setLabel(r.label)} />
        </Box>
        <Box title="Ransomware claims, last 12 months" jump="Claims, 12 months" ui={ui} right={note(ui, "matched by keywords")}>
          <div style={{ fontSize: fs(30), fontWeight: 900, color: B.text, lineHeight: 1.1, marginBottom: 8 }}>{nf(rw.last365)} <span style={{ fontSize: fs(13), fontWeight: 600, color: B.textDim }}>of {nf(rw.total)} in all</span></div>
          {rw.groups.length ? <BarList rows={rw.groups.map(g => ({ label: g.label, value: g.count, href: g.link ? `#/kb/e/${encodeURIComponent(g.link)}` : undefined }))} ui={ui} unit="Claims" maxRows={6} /> : <div style={{ fontSize: fs(12), color: B.textDim }}>{rw.builtAt ? "None in the last 12 months." : "Loading…"}</div>}
        </Box>
        <Box title="US export controls" ui={ui} right={more(ui, "#/kb/export-controls", "Search the lists")}>
          {ec.total ? <>
            <div style={{ fontSize: fs(30), fontWeight: 900, color: B.text, lineHeight: 1.1, marginBottom: 8 }}>{nf(ec.total)} <span style={{ fontSize: fs(13), fontWeight: 600, color: B.textDim }}>listings</span></div>
            <BarList rows={ec.lists.map(l => ({ label: l.name, value: l.count, href: `#/kb/export-controls?list=${l.key}` }))} ui={ui} unit="Listings" keepOrder maxRows={6} />
          </> : <div style={{ fontSize: fs(12), color: B.textDim }}>{ec.error ? `Unavailable (${ec.error})` : "Loading… the lists arrive several minutes after the server starts."}</div>}
        </Box>
      </div>

      <Box title="Who targets the defense industry" jump="Who targets it" ui={ui} about="Each group's ATT&CK description is read by keyword rules: a sentence with a targeting verb and then a defense term. MITRE states it, but the rules can misread a sentence, so the sentence is shown. Groups merely attributed to a military unit (APT28, Sandworm Team) aren't counted." right={<span style={{ display: "flex", gap: 8, alignItems: "center", textTransform: "none", letterSpacing: 0, fontWeight: 500 }}>MITRE's own words <ConfidenceBadge confidence={data.confidence} ui={ui} /></span>}>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
          <button onClick={() => setLabel("")} style={pill(!label)}>All {nf(data.actors.length)}</button>
          {LABELS.map(l => byLabel[l] ? <button key={l} onClick={() => setLabel(l)} style={pill(label === l)}>{l} {nf(byLabel[l])}</button> : null)}
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 640 }}>
            <thead>
              <tr style={{ textAlign: "left", fontSize: fs(10), color: B.textDim, textTransform: "uppercase", letterSpacing: 1 }}>
                <th style={{ padding: "9px 12px", fontWeight: 700 }}>Group or campaign</th>
                <th style={{ padding: "9px 12px", fontWeight: 700 }}>Targets</th>
                <th style={{ padding: "9px 12px", fontWeight: 700 }}>What MITRE ATT&CK says</th>
              </tr>
            </thead>
            <tbody>
              {actors.map(a => (
                <tr key={a.id}>
                  <td style={{ ...cell, minWidth: 170 }}>
                    <a href={entityPath(a)} style={{ display: "inline-flex", gap: 6, alignItems: "center", color: B.text, fontWeight: 700, textDecoration: "none" }}>
                      <TypeIcon type={a.type} color={getConf(a.type).color} size={13} />{a.name}
                    </a>
                    <div style={{ marginTop: 3 }}><IdBadge id={a.attackId} ui={ui} /></div>
                  </td>
                  <td style={cell}><div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>{a.labels.map(l => <Chip key={l} ui={ui}>{l}</Chip>)}</div></td>
                  <td style={{ ...cell, color: B.textMuted, lineHeight: 1.55 }}>“{a.sentence}”</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Box>

      <div style={grid(340)}>
        <Box title="How they get in" ui={ui} right={note(ui, "initial-access techniques, by how many of them use each")}>
          {data.techniques.initial.length ? <BarList rows={techRows(data.techniques.initial)} ui={ui} unit="Groups and campaigns" maxRows={8} /> : <div style={{ fontSize: fs(12), color: B.textDim }}>None recorded.</div>}
        </Box>
        <Box title="What they use most" ui={ui} right={note(ui, "all techniques")}>
          <BarList rows={techRows(data.techniques.overall)} ui={ui} unit="Groups and campaigns" maxRows={10} />
        </Box>
        <Box title="Vulnerabilities they exploit" ui={ui} right={note(ui, "CVEs ATT&CK ties to them")}>
          {data.cves.length ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {data.cves.map(v => (
                <div key={v.name} style={{ display: "flex", gap: 8, alignItems: "center", fontSize: fs(12), minWidth: 0 }}>
                  <a href={entityPath(v)} style={{ color: B.text, fontWeight: 700, textDecoration: "none", whiteSpace: "nowrap" }}>{v.name}</a>
                  <KevBadge kev={v.kev} ui={ui} />
                  <span style={{ color: B.textDim, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }}>{v.snippet}</span>
                  <span style={{ color: B.textMuted, whiteSpace: "nowrap" }}>{nf(v.actors)}</span>
                </div>
              ))}
            </div>
          ) : <div style={{ fontSize: fs(12), color: B.textDim }}>None recorded.</div>}
        </Box>
      </div>

      <Box title="Ransomware claims against defense and aerospace companies" jump="Ransomware claims" ui={ui} right={note(ui, "inferred: matched by keywords, and the claims are the criminals' own")}
        about={<>ransomware.live puts defense in one "Government & Defense" sector, so these are picked out by the words in each victim's company description: aerospace, defense contractor, munitions, shipbuilding, ITAR, the Department of Defense and the like ({rw.terms.slice(0, 5).map(t => `${t.term} ${nf(t.count)}`).join(", ")}). A supplier that names the DoD as a customer counts. They're the gangs' own leak-site claims, not confirmed breaches.</>}>
        {rw.recent.length ? (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 600 }}>
              <thead>
                <tr style={{ textAlign: "left", fontSize: fs(10), color: B.textDim, textTransform: "uppercase", letterSpacing: 1 }}>
                  <th style={{ padding: "9px 12px", fontWeight: 700 }}>Company (claimed)</th>
                  <th style={{ padding: "9px 12px", fontWeight: 700 }}>Group</th>
                  <th style={{ padding: "9px 12px", fontWeight: 700 }}>Matched</th>
                  <th style={{ padding: "9px 12px", fontWeight: 700 }}>Country</th>
                  <th style={{ padding: "9px 12px", fontWeight: 700 }}>Claimed</th>
                </tr>
              </thead>
              <tbody>
                {rw.recent.map(r => (
                  <tr key={r.vid}>
                    <td style={{ ...cell, fontWeight: 600 }}><a href={victimPath(r.vid)} style={{ color: B.text, textDecoration: "none" }}>{r.title}</a></td>
                    <td style={cell}>{r.group.link ? <a href={`#/kb/e/${encodeURIComponent(r.group.link)}`} style={{ color: B.textMuted }}>{r.group.label}</a> : r.group.label}</td>
                    <td style={{ ...cell, color: B.textMuted }}>{r.term}</td>
                    <td style={{ ...cell, color: B.textMuted }}>{r.countryName || "—"}</td>
                    <td style={{ ...cell, color: B.textMuted, whiteSpace: "nowrap" }}>{r.day ? fmtDate(r.day) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <div style={{ fontSize: fs(12), color: B.textDim }}>{rw.builtAt ? "No claims matched." : "Loading… victims arrive a minute or two after the server starts."}</div>}
      </Box>

      <Box title={`Reports on the defense industry (${nf(data.reports.total)})`} jump="Reports" ui={ui} right={note(ui, "by title, summary and tags")}>
        {data.reports.items.length
          ? <div style={grid(300)}>{data.reports.items.map(s => <ReportCard key={s.id} s={s} ui={ui} />)}</div>
          : <div style={{ fontSize: fs(12), color: B.textDim }}>None among the current reports.</div>}
      </Box>
    </div>
  );
}

// ─── The screening lists (#/kb/export-controls, and the bottom of #/kb/missiles) ───

const PAGE = 50;
const LIST_COLOR = { el: "#dc2626", meu: "#ea580c", uvl: "#ca8a04", dtc: "#7c3aed", isn: "#be185d", cmic: "#0369a1", "ofac-wmd": "#9f1239", "un-kp": "#1d4ed8", "un-ir": "#0e7490" };
const ListChip = ({ list, name, ui }) => <Chip ui={ui} color={LIST_COLOR[list]}>{name}</Chip>;

// List cards, search and filters, and the table. hub: "export" or "prolif".
function ListingsBrowser({ ui, params = {}, hub }) {
  const { B, fs } = ui;
  const [q, setQ] = useState(params.q || "");
  const [query, setQuery] = useState(params.q || "");
  const [list, setList] = useState(params.list || "");
  const [country, setCountry] = useState(params.country || "");
  const [regime, setRegime] = useState(params.regime || "");
  const [sort, setSort] = useState(params.sort || "recent");
  const [pages, setPages] = useState([]);
  const [state, setState] = useState({ loading: true, error: "", total: 0, counts: null, lists: [], regimes: null, st: null });
  useEffect(() => { const t = setTimeout(() => setQuery(q.trim()), 300); return () => clearTimeout(t); }, [q]);
  const base = useMemo(() => {
    const u = new URLSearchParams({ hub, sort });
    if (query) u.set("q", query);
    if (list) u.set("list", list);
    if (country) u.set("country", country);
    if (regime) u.set("regime", regime);
    return `/api/kb/export-controls?${u}`;
  }, [hub, query, list, country, regime, sort]);
  useEffect(() => {
    let live = true;
    setState(s => ({ ...s, loading: true, error: "" }));
    getJson(`${base}&offset=0&limit=${PAGE}`, { fresh: true })
      .then(d => { if (!live) return; setPages([d.items]); setState({ loading: false, error: "", total: d.total, counts: d.counts, lists: d.lists, regimes: d.regimes, st: d.state }); })
      .catch(e => live && setState(s => ({ ...s, loading: false, error: e.message })));
    return () => { live = false; };
  }, [base]);
  const items = pages.flat();
  const loadMore = () => getJson(`${base}&offset=${items.length}&limit=${PAGE}`).then(d => setPages(p => [...p, d.items])).catch(() => {});
  const countries = Object.entries(state.counts?.countries || {}).sort((a, b) => b[1] - a[1]);
  const cell = { padding: "9px 12px", borderTop: `1px solid ${B.border}`, fontSize: fs(12), verticalAlign: "top" };
  const prolif = hub === "prolif";
  const searchLabel = prolif ? "Search the proliferation lists" : "Search the export-control lists";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={grid(260)}>
        {state.lists.map(l => (
          <button key={l.key} onClick={() => setList(list === l.key ? "" : l.key)} style={{ ...card(ui), padding: "12px 14px", textAlign: "left", cursor: "pointer", font: "inherit", boxShadow: `inset 3px 0 0 ${LIST_COLOR[l.key]}`, borderColor: list === l.key ? LIST_COLOR[l.key] : B.border }}>
            <div style={{ display: "flex", gap: 8, alignItems: "baseline" }}>
              <span style={{ fontSize: fs(13), fontWeight: 800, color: B.text }}>{l.name}</span>
              <span style={{ marginLeft: "auto", fontSize: fs(12), color: B.textMuted }}>{state.counts?.lists?.[l.key] !== undefined ? nf(state.counts.lists[l.key]) : ""}</span>
            </div>
            <div style={{ fontSize: fs(11), color: B.textDim, margin: "3px 0 5px" }}>{l.agency}</div>
            <div style={{ fontSize: fs(12), color: B.textMuted, lineHeight: 1.5 }}>{l.what}</div>
          </button>
        ))}
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder={prolif ? "Search a company, person, ship or aircraft…" : "Search a company, organisation or person…"} aria-label={searchLabel} style={{ ...control(ui), flex: "1 1 260px", minWidth: 0 }} />
        <select value={list} onChange={e => setList(e.target.value)} aria-label="List" style={control(ui)}>
          <option value="">{state.lists.length ? `All ${state.lists.length === 6 ? "six" : state.lists.length === 4 ? "four" : state.lists.length} lists` : "All lists"}</option>
          {state.lists.map(l => <option key={l.key} value={l.key}>{l.name}</option>)}
        </select>
        {state.regimes && (
          <select value={regime} onChange={e => setRegime(e.target.value)} aria-label="Programme" style={control(ui)}>
            <option value="">Whose programme: all</option>
            {Object.entries(state.regimes).map(([k, name]) => <option key={k} value={k}>{name}{state.counts?.regimes?.[k] !== undefined ? ` (${nf(state.counts.regimes[k])})` : ""}</option>)}
          </select>
        )}
        <select value={country} onChange={e => setCountry(e.target.value)} aria-label="Country" style={control(ui)}>
          <option value="">All countries</option>
          {countries.map(([c, n]) => <option key={c} value={c}>{countryName(c)} ({nf(n)})</option>)}
          {country && !state.counts?.countries?.[country] && <option value={country}>{countryName(country)}</option>}
        </select>
        <select value={sort} onChange={e => setSort(e.target.value)} aria-label="Sort" style={control(ui)}>
          <option value="recent">Sort: Newest listings</option>
          <option value="name">Sort: Name</option>
          {prolif && <option value="links">Sort: Most links</option>}
        </select>
      </div>
      <div style={{ fontSize: fs(11), color: B.textDim }}>{state.loading ? "Loading…" : `${nf(state.total)} listing${state.total === 1 ? "" : "s"}${query ? ` matching “${query}”` : ""}`}{query && !state.loading && !state.total ? `. Not being listed here doesn't clear anyone: check ${prolif ? "OFAC's Sanctions List Search and the UN's consolidated list" : "the official Consolidated Screening List search at trade.gov"} too.` : ""}</div>
      {state.error && <div style={{ color: "#dc2626", fontSize: fs(12) }}>{state.error}</div>}
      {!!items.length && (
        <div style={{ ...card(ui), overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 600 }}>
            <thead>
              <tr style={{ textAlign: "left", fontSize: fs(10), color: B.textDim, textTransform: "uppercase", letterSpacing: 1 }}>
                <th style={{ padding: "9px 12px", fontWeight: 700 }}>Name</th>
                <th style={{ padding: "9px 12px", fontWeight: 700 }}>List</th>
                {prolif && <th style={{ padding: "9px 12px", fontWeight: 700 }}>Programme</th>}
                <th style={{ padding: "9px 12px", fontWeight: 700 }}>Country</th>
                <th style={{ padding: "9px 12px", fontWeight: 700, whiteSpace: "nowrap" }}>Listed</th>
              </tr>
            </thead>
            <tbody>
              {items.map(r => (
                <tr key={r.id}>
                  <td style={{ ...cell, maxWidth: 460 }}>
                    <a href={listingPath(r.id)} style={{ color: B.text, fontWeight: 700, textDecoration: "none" }}>{r.name}</a>
                    {(r.person || r.kind) && <span style={{ marginLeft: 6, fontSize: fs(10), color: B.textDim }}>{r.kind || "person"}</span>}
                    {prolif && r.links > 0 && <span style={{ marginLeft: 6, fontSize: fs(10), color: B.textDim }}>{plural(r.links, "link")}</span>}
                    {r.alt && <div style={{ fontSize: fs(11), color: B.textDim, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>Also: {r.alt}</div>}
                  </td>
                  <td style={cell}><ListChip list={r.list} name={r.listName} ui={ui} /></td>
                  {prolif && <td style={{ ...cell, color: B.textMuted, whiteSpace: "nowrap" }}>{r.regimeName || "—"}</td>}
                  <td style={{ ...cell, color: B.textMuted }}>{r.countries.length ? r.countries.slice(0, 3).map(countryName).join(", ") + (r.countries.length > 3 ? ` +${r.countries.length - 3}` : "") : "—"}</td>
                  <td style={{ ...cell, color: B.textMuted, whiteSpace: "nowrap" }}>{r.start ? fmtDate(r.start) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {items.length < state.total && <button onClick={loadMore} style={{ ...control(ui), cursor: "pointer", alignSelf: "flex-start" }}>Show {Math.min(PAGE, state.total - items.length)} more</button>}
    </div>
  );
}

function ListsState({ st, ui, un }) {
  const { B, fs } = ui;
  return (
    <div style={{ fontSize: fs(11), color: B.textDim }}>
      {st?.builtAt ? `Consolidated Screening List (trade.gov) read ${ago(st.builtAt)}${un ? (st.unBuiltAt ? ` · UN list read ${ago(st.unBuiltAt)}` : st.unError ? ` · UN list unavailable (${st.unError})` : "") : ""} · refreshed daily` : st?.error ? `Unavailable (${st.error})` : "Loading… the lists arrive several minutes after the server starts"}
    </div>
  );
}

export function KbExportControls({ ui, params = {} }) {
  const { B } = ui;
  const { data } = useJson("/api/kb/export-controls?hub=export&limit=1");
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div>
        <Heading slug="export-controls" ui={ui}>
          The US government's lists of companies, organisations and people that US exports are restricted to, or that may not export US defense items themselves: who's buying for weapons programmes and foreign militaries, and who's been caught smuggling arms technology.
          Search a name against all six. The US, UN, EU, UK, Australian and Canadian sanctions lists are under <a href="#/kb/sanctions" style={{ color: B.accent }}>Sanctions</a>, the networks behind North Korea's and Iran's weapons programmes under <a href="#/kb/missiles" style={{ color: B.accent }}>Missiles &amp; Proliferation</a>, and OFAC's listings with crypto addresses in <a href="#/kb/sanctioned" style={{ color: B.accent }}>Follow the Money</a>.
        </Heading>
        <ListsState st={data?.state} ui={ui} />
      </div>
      <ListingsBrowser ui={ui} params={params} hub="export" />
    </div>
  );
}

// ─── #/kb/missiles ───

const usd = (n) => (n >= 1e9 ? `$${(n / 1e9).toFixed(n % 1e9 ? 1 : 0)} billion` : `$${Math.round(n / 1e6)} million`);
// Dates kept by hand may give only the month ("2024-05").
const fmtDay = (d) => (/^\d{4}-\d{2}$/.test(d || "") ? new Date(`${d}-15`).toLocaleDateString(undefined, { year: "numeric", month: "short" }) : fmtDate(d));
const PROLIF_LABELS = ["Nuclear", "Missiles", "Weapons", "Sanctions"];
// The map's country, filters and years in the address, so a link opens the same map.
// replaceState doesn't fire hashchange, so the page isn't reloaded.
const MAP_KEYS = ["country", "class", "family", "missile", "outcome", "years", "view", "combat", "mode", "paths", "ix"];
function keepMapInHash(state) {
  const [route, query = ""] = window.location.hash.split("?");
  const q = new URLSearchParams(query);
  for (const k of MAP_KEYS) { if (state[k]) q.set(k, state[k]); else q.delete(k); }
  const next = `${route}${q.toString() ? `?${q}` : ""}`;
  if (next !== window.location.hash) window.history.replaceState(null, "", next);
}

export function KbMissiles({ ui, params = {} }) {
  const { B, fs, getConf, TypeIcon } = ui;
  const { data, error, loading } = useJson("/api/kb/defense/missiles");
  if (loading) return <><Heading slug="missiles" ui={ui} /><div style={{ color: B.textDim, fontSize: fs(13) }}>Loading…</div></>;
  if (error) return <><Heading slug="missiles" ui={ui} /><div style={{ color: "#dc2626", fontSize: fs(12) }}>{error}</div></>;
  const L = data.lists;
  const cell = { padding: "9px 12px", borderTop: `1px solid ${B.border}`, fontSize: fs(12), verticalAlign: "top" };
  const th = { padding: "9px 12px", fontWeight: 700 };
  const head = (...cols) => <thead><tr style={{ textAlign: "left", fontSize: fs(10), color: B.textDim, textTransform: "uppercase", letterSpacing: 1 }}>{cols.map(c => <th key={c} style={th}>{c}</th>)}</tr></thead>;
  const groupChips = (groups) => groups.length ? <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>{groups.map(g => <a key={g.id} href={entityPath(g)} style={{ textDecoration: "none" }}><Chip ui={ui} color={getConf(g.type).color}>{g.name}</Chip></a>)}</div> : null;
  const loadingLists = !L.total && <div style={{ fontSize: fs(12), color: B.textDim }}>{L.error ? `Unavailable (${L.error})` : "Loading… the lists arrive several minutes after the server starts."}</div>;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div>
        <Heading slug="missiles" ui={ui} about="This page brings together those networks from OFAC's and the UN's lists, the official findings that North Korea's hacking and crypto theft pay for its weapons, the thefts the FBI has attributed to it, the groups MITRE ATT&CK says go after weapons programmes, and twelve countries' missile tests on a map.">
          North Korea and Iran build their nuclear weapons, ballistic missiles and drones with parts, money and shipping bought abroad through front companies, banks, ships and middlemen, and the US and UN sanction them as they're found.
        </Heading>
        <StixBar apiKey="prolif" ui={ui} />
        <OnThisPage ui={ui} />
      </div>

      <div style={grid(280)}>
        <Box title="Sanctioned for weapons programmes" jump="Sanctioned" ui={ui}>
          {L.total ? <>
            <div style={{ fontSize: fs(30), fontWeight: 900, color: B.text, lineHeight: 1.1, marginBottom: 8 }}>{nf(L.total)} <span style={{ fontSize: fs(13), fontWeight: 600, color: B.textDim }}>listings</span></div>
            <BarList rows={L.lists.map(l => ({ label: l.name, value: l.count, href: `#/kb/missiles?list=${l.key}` }))} ui={ui} unit="Listings" keepOrder />
          </> : loadingLists}
        </Box>
        <Box title="Whose programme" ui={ui} right={note(ui, "by programme, or the listing's country")}>
          {L.total ? <BarList rows={L.regimes.filter(r => r.count).map(r => ({ label: r.name, value: r.count, href: `#/kb/missiles?regime=${r.key}` }))} ui={ui} unit="Listings" keepOrder /> : loadingLists}
        </Box>
        <Box title="Stolen by North Korea" jump="Stolen" ui={ui} right={note(ui, "five thefts the FBI attributes")}>
          <div style={{ fontSize: fs(30), fontWeight: 900, color: B.text, lineHeight: 1.1, marginBottom: 8 }}>{usd(data.thefts.totalUsd)}</div>
          <BarList rows={data.thefts.items.map(t => ({ label: t.victim, value: Math.round(t.usd / 1e6) }))} ui={ui} unit="US$ million" />
        </Box>
      </div>

      <Box title="Missile tests and launches" jump="Missile map" ui={ui} right={note(ui, "Twelve countries, from the CNS databases and GCAT: launch sites, flights and where they came down, by year")}>
        <Suspense fallback={<div style={{ color: B.textDim, fontSize: fs(13) }}>Loading the map…</div>}>
          <MissileMap ui={ui} thefts={data.thefts.items} initial={params} onState={keepMapInHash} />
        </Suspense>
      </Box>

      <Box title="How hacking pays for missiles" jump="Hacking pays for missiles" ui={ui} right={note(ui, "official findings, quoted")}>
        <div style={grid(300)}>
          {data.funding.map(f => (
            <div key={f.url} style={{ borderLeft: `3px solid ${B.primary}`, padding: "2px 0 2px 12px", minWidth: 0 }}>
              <div style={{ fontSize: fs(11), color: B.textDim, marginBottom: 4 }}>{f.source} · {fmtDate(f.date)}</div>
              {f.quote && <div style={{ fontSize: fs(13), color: B.text, lineHeight: 1.6, marginBottom: 6 }}>“{f.quote}”</div>}
              <div style={{ fontSize: fs(12), color: B.textMuted, lineHeight: 1.55, marginBottom: 6 }}>{f.about}</div>
              <a href={f.url} target="_blank" rel="noreferrer noopener" style={{ fontSize: fs(12), color: B.accent }}>{f.title} ↗</a>
            </div>
          ))}
        </div>
      </Box>

      <Box title="Thefts the FBI attributes to North Korea" jump="FBI thefts" ui={ui} about={<>TraderTraitor (also Jade Sleet, UNC4899, Slow Pisces) isn't a group in ATT&CK, so it isn't linked. The addresses OFAC lists for Lazarus Group are on <a href={sanctionedPath("27307")} style={{ color: B.accent }}>its page in Follow the Money</a>.</>} right={<span style={{ display: "flex", gap: 8, alignItems: "center", textTransform: "none", letterSpacing: 0, fontWeight: 500 }}>kept by hand, each with the FBI's statement <ConfidenceBadge confidence={data.thefts.confidence} ui={ui} /></span>}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 640 }}>
            {head("Victim", "Stolen", "Who, as the FBI names them", "When", "FBI statement")}
            <tbody>
              {data.thefts.items.map(t => (
                <tr key={t.url}>
                  <td style={{ ...cell, fontWeight: 700, color: B.text }}>{t.victim}</td>
                  <td style={{ ...cell, whiteSpace: "nowrap" }}>{usd(t.usd)} <span style={{ color: B.textDim }}>in {t.asset}</span></td>
                  <td style={cell}><div style={{ color: B.textMuted, marginBottom: t.groups.length ? 5 : 0 }}>{t.actors.join(", ")}</div>{groupChips(t.groups)}</td>
                  <td style={{ ...cell, color: B.textMuted, whiteSpace: "nowrap" }}>{fmtDay(t.date)}</td>
                  <td style={{ ...cell, whiteSpace: "nowrap" }}><a href={t.url} target="_blank" rel="noreferrer noopener" style={{ color: B.accent }}>{fmtDate(t.said)} ↗</a></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Box>

      <Box title={`Sanctioned hackers, launderers and crypto (${nf(data.cyber.total)})`} jump="Hackers and launderers" ui={ui} about="Listings in these programmes that are hacking groups (their name or an alias is an ATT&CK group's), were also sanctioned under OFAC's cyber programmes, or have crypto addresses on the list: North Korea's hacking units, the people and mixers that launder their takings, the IT workers who earn for the regime, and others." right={<span style={{ display: "flex", gap: 8, alignItems: "center", textTransform: "none", letterSpacing: 0, fontWeight: 500 }}>group names matched to ATT&CK <ConfidenceBadge confidence={data.cyber.confidence} ui={ui} /></span>}>
        {data.cyber.items.length ? (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 640 }}>
              {head("Listing", "Programme", "ATT&CK group", "Crypto addresses", "Listed")}
              <tbody>
                {data.cyber.items.map(r => (
                  <tr key={r.id}>
                    <td style={{ ...cell, maxWidth: 380 }}>
                      <a href={listingPath(r.id)} style={{ color: B.text, fontWeight: 700, textDecoration: "none" }}>{r.name}</a>
                      {r.person && <span style={{ marginLeft: 6, fontSize: fs(10), color: B.textDim }}>person</span>}
                      <div style={{ marginTop: 4 }}><ListChip list={r.list} name={r.listName} ui={ui} /></div>
                    </td>
                    <td style={{ ...cell, color: B.textMuted, whiteSpace: "nowrap" }}>{r.regimeName || "—"}</td>
                    <td style={cell}>{r.group ? groupChips([r.group]) : <span style={{ color: B.textDim }}>—</span>}</td>
                    <td style={{ ...cell, whiteSpace: "nowrap" }}>{r.wallets ? <a href={sanctionedPath(r.ent)} style={{ color: B.accent }}>{plural(r.wallets, "address", "addresses")}</a> : <span style={{ color: B.textDim }}>—</span>}</td>
                    <td style={{ ...cell, color: B.textMuted, whiteSpace: "nowrap" }}>{r.start ? fmtDate(r.start) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : loadingLists || <div style={{ fontSize: fs(12), color: B.textDim }}>None found.</div>}
      </Box>

      <div style={grid(400)}>
        <Box title="The biggest networks" ui={ui} about="Listings with the most links to others: who OFAC says each listing is “Linked To”, and the entities the UN's reasons for listing name. Open one to see its network." right={<span style={{ display: "flex", gap: 6, alignItems: "center", textTransform: "none", letterSpacing: 0, fontWeight: 500 }}>OFAC <ConfidenceBadge confidence={data.networks.confidence.ofac} ui={ui} /> UN <ConfidenceBadge confidence={data.networks.confidence.un} ui={ui} /></span>}>
          {data.networks.items.length ? data.networks.items.map(r => (
            <div key={r.id} style={{ display: "flex", gap: 8, alignItems: "center", fontSize: fs(12), padding: "6px 0", borderTop: `1px solid ${B.border}`, minWidth: 0 }}>
              <a href={listingPath(r.id)} style={{ color: B.text, fontWeight: 700, textDecoration: "none", flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.name}</a>
              <ListChip list={r.list} name={r.list === "ofac-wmd" ? "OFAC" : r.list === "un-kp" ? "UN · North Korea" : r.list === "un-ir" ? "UN · Iran" : r.listName} ui={ui} />
              <span style={{ color: B.textMuted, whiteSpace: "nowrap" }}>{plural(r.links, "link")}</span>
            </div>
          )) : loadingLists || <div style={{ fontSize: fs(12), color: B.textDim }}>None.</div>}
        </Box>
        <Box title="Who targets weapons programmes" ui={ui} about="Read from each ATT&CK description by keyword rules: a sentence with a targeting verb and then a nuclear, missile, weapons or sanctions term. The chemical-weapons watchdog (OPCW) counts." right={<span style={{ display: "flex", gap: 8, alignItems: "center", textTransform: "none", letterSpacing: 0, fontWeight: 500 }}>MITRE's own words <ConfidenceBadge confidence={data.actorConfidence} ui={ui} /></span>}>
          {data.actors.map(a => (
            <div key={a.id} style={{ padding: "8px 0", borderTop: `1px solid ${B.border}` }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 4 }}>
                <a href={entityPath(a)} style={{ display: "inline-flex", gap: 6, alignItems: "center", color: B.text, fontWeight: 700, textDecoration: "none", fontSize: fs(12) }}><TypeIcon type={a.type} color={getConf(a.type).color} size={13} />{a.name}</a>
                <IdBadge id={a.attackId} ui={ui} />
                {a.labels.filter(l => PROLIF_LABELS.includes(l)).map(l => <Chip key={l} ui={ui}>{l}</Chip>)}
              </div>
              <div style={{ fontSize: fs(12), color: B.textMuted, lineHeight: 1.55 }}>“{a.sentence}”</div>
            </div>
          ))}
        </Box>
      </div>

      <Box title={`Reports on North Korea's hacking and on proliferation (${nf(data.reports.total)})`} jump="Reports" ui={ui} right={note(ui, "by title, summary and tags")}>
        {data.reports.items.length
          ? <div style={grid(300)}>{data.reports.items.map(s => <ReportCard key={s.id} s={s} ui={ui} />)}</div>
          : <div style={{ fontSize: fs(12), color: B.textDim }}>None among the current reports.</div>}
      </Box>

      <div data-box="The proliferation lists" style={{ scrollMarginTop: 12 }}>
        <div style={{ ...sectionLabel(ui), margin: "4px 0 6px" }}>The proliferation lists</div>
        <ListsState st={L.builtAt ? { builtAt: L.builtAt, unBuiltAt: L.unBuiltAt, unError: L.unError } : { error: L.error }} ui={ui} un />
      </div>
      <ListingsBrowser ui={ui} params={params} hub="prolif" />
    </div>
  );
}

// ─── #/kb/listing/<id> ───

function LinkRows({ rows, ui }) {
  const { B, fs } = ui;
  return rows.map(s => (
    <div key={s.id} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6, fontSize: fs(12), minWidth: 0, flexWrap: "wrap" }}>
      <ListChip list={s.list} name={s.listName} ui={ui} />
      <a href={listingPath(s.id)} style={{ color: B.text, fontWeight: 600 }}>{s.name}</a>
      {(s.person || s.kind) && <span style={{ color: B.textDim, fontSize: fs(10) }}>{s.kind || "person"}</span>}
    </div>
  ));
}

export function KbListing({ id, ui }) {
  const { B, fs, TypeIcon, getConf } = ui;
  const { data, error, loading } = useJson(`/api/kb/listing/${encodeURIComponent(id)}`);
  const sanctions = /^(?:ofac|un)-/.test(String(id));
  const tab = sanctions ? ["#/kb/missiles", "Missiles & Proliferation"] : ["#/kb/export-controls", "Export Controls"];
  const section = ["#/kb/countries", "Geopolitics"];
  if (loading) return <div style={{ color: B.textDim, fontSize: fs(13) }}>Loading…</div>;
  if (error) return <div style={{ ...card(ui), padding: 20, color: B.textMuted, fontSize: fs(13) }}>{error} <a href={tab[0]} style={{ color: B.accent }}>Back to the lists</a></div>;
  const color = LIST_COLOR[data.list];
  const line = (k, v) => (v ? <div style={{ fontSize: fs(12), marginBottom: 6, lineHeight: 1.55 }}><span style={{ color: B.textDim }}>{k}: </span><span style={{ color: B.text }}>{v}</span></div> : null);
  const what = data.kind ? data.kind[0].toUpperCase() + data.kind.slice(1) : data.person ? "Person" : "Organisation";
  const un = /^un-/.test(data.list);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ fontSize: fs(12), color: B.textDim }}><a href={section[0]} style={{ color: B.textDim, textDecoration: "none" }}>{section[1]}</a> / <a href={tab[0]} style={{ color: B.textDim, textDecoration: "none" }}>{tab[1]}</a> / {data.name}</div>
      <div style={{ ...card(ui), borderTop: `3px solid ${color}`, padding: "20px 22px" }}>
        <div style={{ display: "flex", gap: 14, alignItems: "flex-start", flexWrap: "wrap" }}>
          <div style={{ width: 44, height: 44, borderRadius: 10, background: color + "18", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><TypeIcon type="identity" color={getConf("identity").color} size={22} /></div>
          <div style={{ flex: "1 1 320px", minWidth: 0 }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <span style={{ fontSize: fs(11), color, fontWeight: 800, textTransform: "uppercase", letterSpacing: 1.2 }}>{what} · {data.hub === "prolif" ? "sanctions listing" : "export-control listing"}</span>
              <ListChip list={data.list} name={data.listName} ui={ui} />
              {data.sameName.map(s => s.list).filter((l, i, a) => l !== data.list && a.indexOf(l) === i).map(l => <ListChip key={l} list={l} name={`also ${data.sameName.find(s => s.list === l).listName}`} ui={ui} />)}
            </div>
            <h1 style={{ fontSize: fs(22), fontWeight: 800, margin: "6px 0 6px", color: B.text }}>{data.name}</h1>
            <div style={{ fontSize: fs(12), color: B.textMuted }}>
              {data.start ? `Listed ${fmtDate(data.start)}` : "Listing date not given"}{data.end ? ` · until ${fmtDate(data.end)}` : ""}
              {data.countries.length ? ` · ${data.countries.map(countryName).join(", ")}` : ""}{data.regimeName && data.regime !== "other" ? ` · ${data.regimeName}'s programme` : ""} · {data.agency}{data.ref ? ` · ${data.ref}` : ""}
            </div>
          </div>
        </div>
        <StixBar apiKey={data.key} ui={ui} />
      </div>

      <div style={grid(400)}>
        <div style={{ display: "flex", flexDirection: "column", gap: 14, minWidth: 0 }}>
          <Box title="What it means" ui={ui}>
            <div style={{ fontSize: fs(13), color: B.text, lineHeight: 1.6, marginBottom: 10 }}>{data.listWhat}</div>
            {line("Licence requirement", data.licenseRequirement)}
            {line("Licence policy", data.licensePolicy)}
            {data.programLabels?.length ? line("Programmes", data.programLabels.map(p => (p.label === p.code ? p.code : `${p.label} [${p.code}]`)).join("; ")) : line("Programme", data.programs)}
            {line(un ? "Reasons for listing" : "Remarks", data.remarks)}
            {line(un ? "Designation" : "Title", data.title)}
          </Box>
          {data.group && (
            <Box title="The hacking group" ui={ui} right={note(ui, "matched by name")}>
              <div style={{ fontSize: fs(12), color: B.textMuted, lineHeight: 1.55, marginBottom: 8 }}>This listing's name or an alias is an ATT&CK group's:</div>
              <a href={entityPath(data.group)} style={{ textDecoration: "none" }}><Chip ui={ui} color={getConf(data.group.type).color}>{data.group.name}</Chip></a>
            </Box>
          )}
          {(data.links?.length > 0 || data.unlinked?.length > 0) && (
            <Box title={un ? "Its UN listing names" : "OFAC says it's linked to"} ui={ui} right={note(ui, un ? "names found in the reasons" : "OFAC's “Linked To”")}>
              <LinkRows rows={data.links} ui={ui} />
              {data.unlinked?.length > 0 && <div style={{ fontSize: fs(12), color: B.textMuted, marginTop: data.links.length ? 8 : 0, lineHeight: 1.5 }}><span style={{ color: B.textDim }}>Not on these lists: </span>{data.unlinked.join("; ")}</div>}
            </Box>
          )}
          {data.linkedFrom?.length > 0 && (
            <Box title={`Linked to it (${nf(data.linkedFromTotal)})`} ui={ui} right={note(ui, un ? "UN listings that name it" : "OFAC listings “Linked To” it")}>
              <LinkRows rows={data.linkedFrom} ui={ui} />
              {data.linkedFromTotal > data.linkedFrom.length && <div style={{ fontSize: fs(11), color: B.textDim }}>and {nf(data.linkedFromTotal - data.linkedFrom.length)} more</div>}
            </Box>
          )}
          {data.addresses.length > 0 && (
            <Box title={`Addresses (${data.addresses.length})`} ui={ui}>
              {data.addresses.map((a, i) => <div key={i} style={{ fontSize: fs(12), color: B.textMuted, marginBottom: 5, lineHeight: 1.5 }}>{a}</div>)}
            </Box>
          )}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 14, minWidth: 0 }}>
          {data.altNames.length > 0 && (
            <Box title="Also known as" ui={ui}>
              <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>{data.altNames.map(a => <Chip key={a} ui={ui}>{a}</Chip>)}</div>
            </Box>
          )}
          {data.wallets > 0 && (
            <Box title="Crypto addresses" ui={ui}>
              <a href={sanctionedPath(data.ent)} style={{ fontSize: fs(12), color: B.accent }}>{plural(data.wallets, "address", "addresses")} OFAC lists for it, in Follow the Money →</a>
            </Box>
          )}
          {data.sameName.length > 0 && (
            <Box title="The same name on the lists" ui={ui} right={note(ui, "exact name or alias")}>
              {data.sameName.map(s => <div key={s.id} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6, fontSize: fs(12) }}><ListChip list={s.list} name={s.listName} ui={ui} /><a href={listingPath(s.id)} style={{ color: B.text, fontWeight: 600 }}>{s.name}</a>{s.start && <span style={{ color: B.textDim }}>{fmtDate(s.start)}</span>}</div>)}
            </Box>
          )}
          {(data.frLinks.length > 0 || data.sourceUrl || data.ofacUrl) && (
            <Box title="Sources" ui={ui}>
              {data.ofacUrl && <div style={{ fontSize: fs(12), marginBottom: 5 }}><a href={data.ofacUrl} target="_blank" rel="noreferrer noopener" style={{ color: B.accent }}>OFAC Sanctions List Search: entry {data.ent} ↗</a></div>}
              {data.frLinks.map(f => <div key={f.cite} style={{ fontSize: fs(12), marginBottom: 5 }}>{f.url ? <a href={f.url} target="_blank" rel="noreferrer noopener" style={{ color: B.accent }}>Federal Register {f.cite} ↗</a> : `Federal Register ${f.cite}`}</div>)}
              {data.sourceUrl && <div style={{ fontSize: fs(12), marginBottom: 5 }}><a href={data.sourceUrl} target="_blank" rel="noreferrer noopener" style={{ color: B.accent }}>{un ? `${data.listName}: the committee` : data.listName} ↗</a></div>}
              {data.infoUrl && data.infoUrl !== data.sourceUrl && <div style={{ fontSize: fs(12) }}><a href={data.infoUrl} target="_blank" rel="noreferrer noopener" style={{ color: B.accent }}>{un ? "The UN Security Council consolidated list" : "About this list"} ↗</a></div>}
            </Box>
          )}
          {data.ids.length > 0 && (
            <Box title="Identifiers" ui={ui}>
              {data.ids.map((x, i) => <div key={i} style={{ fontSize: fs(12), color: B.textMuted, marginBottom: 4, wordBreak: "break-word" }}>{x}</div>)}
            </Box>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── #/kb/space ───

const incidentPath = (id) => `#/kb/space?incident=${encodeURIComponent(id)}`;
const CVES_SHOWN = 20;

// SPARTA's nine tactics, with the techniques the incidents are cited under. With an
// incident picked, its techniques stand out and the rest fade.
function SpartaCoverage({ matrix, sel, incidents, ui }) {
  const { B, fs } = ui;
  const picked = sel ? new Set((incidents.find(i => i.id === sel)?.sparta || []).map(t => t.id)) : null;
  const names = Object.fromEntries(incidents.map(i => [i.id, i.name]));
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 150px), 1fr))", gap: 8 }}>
      {matrix.map(t => (
        <div key={t.shortname} style={{ background: B.bg, border: `1px solid ${B.border}`, borderRadius: 8, padding: "8px 9px", minWidth: 0 }}>
          <a href={`#/kb/frameworks/sparta`} style={{ display: "block", fontSize: fs(11), fontWeight: 800, color: B.text, textDecoration: "none" }}>{t.name}</a>
          <div style={{ fontSize: fs(10), color: B.textDim, marginBottom: 7 }}>{nf(t.cited.length)} of {nf(t.techniques)} cited</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {t.cited.map(c => {
              const on = !picked || picked.has(c.id);
              return (
                <a key={c.id} href={entityPath(c)} title={`${c.attackId} ${c.name}\nCited for: ${c.incidents.map(i => names[i]).join("; ")}`}
                  style={{ display: "flex", gap: 5, alignItems: "baseline", fontSize: fs(11), lineHeight: 1.35, textDecoration: "none", color: on ? B.text : B.textDim, opacity: on ? 1 : 0.45, background: picked && on ? B.primary + "1c" : "transparent", border: `1px solid ${picked && on ? B.primary + "66" : "transparent"}`, borderRadius: 5, padding: "2px 4px" }}>
                  <span style={{ fontFamily: "monospace", fontSize: fs(10), color: B.textMuted, whiteSpace: "nowrap" }}>{c.attackId}</span>
                  <span style={{ flex: 1, minWidth: 0 }}>{c.name}</span>
                  {c.incidents.length > 1 && <span style={{ fontSize: fs(10), color: B.textDim }}>×{c.incidents.length}</span>}
                </a>
              );
            })}
            {!t.cited.length && <span style={{ fontSize: fs(11), color: B.textDim }}>None cited</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

function IncidentCard({ inc, ui, on, onPick }) {
  const { B, fs, getConf } = ui;
  const tactics = [];
  for (const t of inc.sparta) {
    if (!tactics.length || tactics[tactics.length - 1].name !== t.tactic) tactics.push({ name: t.tactic, items: [] });
    tactics[tactics.length - 1].items.push(t);
  }
  return (
    <div id={`incident-${inc.id}`} style={{ ...card(ui), padding: "16px 20px", minWidth: 0, borderLeft: `3px solid ${on ? B.primary : B.border}`, scrollMarginTop: 12 }}>
      <div style={{ display: "flex", gap: 10, alignItems: "baseline", flexWrap: "wrap", marginBottom: 4 }}>
        <span style={{ fontSize: fs(15), fontWeight: 800, color: B.text }}>{inc.name}</span>
        <span style={{ fontSize: fs(12), color: B.textDim }}>{inc.when}</span>
        <button onClick={() => onPick(on ? null : inc.id)} style={{ ...ui.HP.navBtn, marginLeft: "auto", padding: "4px 10px", fontSize: fs(11), ...(on ? { color: B.text, borderColor: B.primary, background: B.primary + "14" } : {}) }}>{on ? "Showing in SPARTA ✓" : "Show in SPARTA"}</button>
      </div>
      <div style={{ fontSize: fs(12), color: B.textMuted, marginBottom: 8 }}><b style={{ color: B.text, fontWeight: 600 }}>{inc.victim}</b> · {inc.hit}</div>
      <p style={{ fontSize: fs(13), color: B.text, lineHeight: 1.6, margin: "0 0 10px", maxWidth: "110ch" }}>{inc.what}</p>
      {inc.quote && <div style={{ borderLeft: `3px solid ${B.border}`, padding: "2px 0 2px 12px", fontSize: fs(12), color: B.textMuted, lineHeight: 1.55, marginBottom: 10, maxWidth: "110ch" }}>“{inc.quote.text}” <span style={{ color: B.textDim }}>— {inc.quote.by}</span></div>}
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", fontSize: fs(12), marginBottom: 10 }}>
        <span style={{ color: B.textDim, fontSize: fs(10), textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>Who</span>
        {inc.attribution ? <>
          <span style={{ color: B.text, fontWeight: 600 }}>{inc.attribution.actor}</span>
          <span style={{ color: B.textMuted }}>according to {inc.attribution.by}{inc.attribution.date ? ` (${fmtDate(inc.attribution.date)})` : ""}</span>
          <ConfidenceBadge confidence={inc.attribution.confidence} ui={ui} />
        </> : <span style={{ color: B.textMuted }}>Not attributed. {inc.unattributed}</span>}
      </div>
      {inc.attribution?.text && <div style={{ fontSize: fs(12), color: B.textMuted, margin: "-4px 0 10px", lineHeight: 1.5 }}>“{inc.attribution.text}”</div>}
      {inc.attack.length > 0 && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", marginBottom: 10 }}>
          <span style={{ color: B.textDim, fontSize: fs(10), textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>ATT&CK</span>
          {inc.attack.map(a => <a key={a.id} href={entityPath(a)} title={a.why} style={{ textDecoration: "none" }}><Chip ui={ui} color={getConf(a.type).color}>{a.name} · {a.attackId}</Chip></a>)}
        </div>
      )}
      <div style={{ marginBottom: 10 }}>
        <div style={{ color: B.textDim, fontSize: fs(10), textTransform: "uppercase", letterSpacing: 1, fontWeight: 700, marginBottom: 5 }}>SPARTA techniques that cite it</div>
        {tactics.length ? tactics.map(g => (
          <div key={g.name} style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "baseline", marginBottom: 4, fontSize: fs(12) }}>
            <span style={{ color: B.textMuted, minWidth: 120 }}>{g.name}</span>
            {g.items.map(t => <a key={t.id} href={entityPath(t)} title={`SPARTA cites: ${t.ref.title}`} style={{ textDecoration: "none" }}><Chip ui={ui}>{t.attackId} {t.name}</Chip></a>)}
          </div>
        )) : <span style={{ fontSize: fs(12), color: B.textDim }}>SPARTA doesn't cite this one yet.</span>}
      </div>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
        {inc.sources.map(s => <a key={s.url} href={s.url} target="_blank" rel="noreferrer noopener" style={{ fontSize: fs(12), color: B.accent }}>{s.publisher}: {s.title} ({fmtDay(s.date)}) ↗</a>)}
        <span style={{ marginLeft: "auto" }}><StixExportLink href={stixEntityUrl(`space-incident:${inc.id}`)} ui={ui} label="STIX" title="Download this incident as a STIX 2.1 bundle: a campaign with its SPARTA techniques and who it's attributed to" /></span>
      </div>
    </div>
  );
}

export function KbSpace({ ui, params = {} }) {
  const { B, fs, getConf, TypeIcon } = ui;
  const { data, error, loading } = useJson("/api/kb/defense/space");
  const [sel, setSel] = useState(params.incident || null);
  const [segment, setSegment] = useState("");
  const [allCves, setAllCves] = useState(false);
  useEffect(() => {
    if (!data || !params.incident) return;
    const el = document.getElementById(`incident-${params.incident}`);
    if (el) el.scrollIntoView({ block: "start" });
  }, [data, params.incident]);
  if (loading) return <><Heading slug="space" ui={ui} /><div style={{ color: B.textDim, fontSize: fs(13) }}>Loading…</div></>;
  if (error) return <><Heading slug="space" ui={ui} /><div style={{ color: "#dc2626", fontSize: fs(12) }}>{error}</div></>;
  const cv = data.cves;
  const cveRows = segment ? cv.items.filter(c => c.segment === segment) : cv.items;
  const cited = data.matrix.reduce((s, t) => s + t.cited.length, 0);
  const rw = data.ransomware;
  const cell = { padding: "9px 12px", borderTop: `1px solid ${B.border}`, fontSize: fs(12), verticalAlign: "top" };
  const head = (...cols) => <thead><tr style={{ textAlign: "left", fontSize: fs(10), color: B.textDim, textTransform: "uppercase", letterSpacing: 1 }}>{cols.map(c => <th key={c} style={{ padding: "9px 12px", fontWeight: 700 }}>{c}</th>)}</tr></thead>;
  const pill = (on) => ({ ...ui.HP.navBtn, padding: "5px 11px", fontSize: fs(12), ...(on ? { color: B.text, borderColor: B.primary, background: B.primary + "14", fontWeight: 700 } : {}) });
  const pick = (id) => {
    setSel(id);
    if (id) document.getElementById("sparta-coverage")?.scrollIntoView({ block: "start" });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div>
        <Heading slug="space" ui={ui} about="This page brings together incidents kept by hand, each mapped to SPARTA (The Aerospace Corporation's matrix of attacks on space systems) by the techniques that cite it, who targets the sector, vulnerabilities in satellite and space products, North Korea's satellite launches, ransomware claims against space companies, and reports.">
          Satellites are run from the ground: operators' networks, ground stations, the management systems of satellite broadband, and the terminals and modems customers use. That's where the real attacks have landed, from the outage that cut Viasat's KA-SAT network as Russia invaded Ukraine to espionage inside satellite operators and NASA.
        </Heading>
        <StixBar apiKey="space" ui={ui} />
        <OnThisPage ui={ui} />
      </div>

      <div style={grid(260)}>
        <Box title="Incidents kept here" jump="Incidents" ui={ui} right={note(ui, "each checked against its source")}>
          <div style={{ fontSize: fs(30), fontWeight: 900, color: B.text, lineHeight: 1.1, marginBottom: 8 }}>{nf(data.incidents.length)}</div>
          <div style={{ fontSize: fs(12), color: B.textMuted, lineHeight: 1.55 }}>{nf(data.incidents.filter(i => i.attribution).length)} attributed, from government statements to unnamed sources; {nf(data.incidents.filter(i => !i.attribution).length)} with no one named.</div>
        </Box>
        <Box title="SPARTA techniques cited" jump="SPARTA" ui={ui} right={more(ui, "#/kb/frameworks/sparta", "The matrix")}>
          <div style={{ fontSize: fs(30), fontWeight: 900, color: B.text, lineHeight: 1.1, marginBottom: 8 }}>{nf(cited)} <span style={{ fontSize: fs(13), fontWeight: 600, color: B.textDim }}>of {nf(data.spartaTotal)}</span></div>
          <div style={{ fontSize: fs(12), color: B.textMuted, lineHeight: 1.55 }}>SPARTA{data.spartaVersion ? ` ${data.spartaVersion}` : ""} techniques and sub-techniques whose references cite one of these incidents. Many of the rest have only been shown in labs.</div>
        </Box>
        <Box title="Vulnerabilities in satellite products" jump="Vulnerabilities" ui={ui} right={note(ui, "from the NVD")}>
          {cv.total ? <>
            <div style={{ fontSize: fs(30), fontWeight: 900, color: B.text, lineHeight: 1.1, marginBottom: 8 }}>{nf(cv.total)} <span style={{ fontSize: fs(13), fontWeight: 600, color: B.textDim }}>CVEs</span></div>
            <BarList rows={cv.bySegment.filter(s => s.count).map(s => ({ label: s.label, value: s.count }))} ui={ui} unit="CVEs" keepOrder onSelect={(r) => { setSegment(r.label); document.getElementById("space-cves")?.scrollIntoView({ block: "start" }); }} />
          </> : <div style={{ fontSize: fs(12), color: B.textDim }}>{cv.state.error ? `Unavailable (${cv.state.error})` : "Loading… these arrive after the day's CVE download, some minutes after the server starts."}</div>}
        </Box>
      </div>

      {data.findings.map(f => (
        <Box key={f.url} title="The bigger picture" ui={ui} right={note(ui, `${f.source}, ${fmtDate(f.date)}`)}>
          <div style={{ borderLeft: `3px solid ${B.primary}`, padding: "2px 0 2px 12px" }}>
            <div style={{ fontSize: fs(14), color: B.text, lineHeight: 1.6, marginBottom: 6 }}>“{f.quote}”</div>
            <div style={{ fontSize: fs(12), color: B.textMuted, lineHeight: 1.55, marginBottom: 6, maxWidth: "110ch" }}>{f.about}</div>
            <a href={f.url} target="_blank" rel="noreferrer noopener" style={{ fontSize: fs(12), color: B.accent }}>{f.title} ↗</a>
          </div>
        </Box>
      ))}

      <div id="sparta-coverage" style={{ scrollMarginTop: 12 }}>
        <Box title="Where the incidents sit in SPARTA" jump="Incidents in SPARTA" ui={ui} about="SPARTA has no list of incidents, but its techniques cite reports of real ones as examples. A technique is shown under an incident when one of its references is a report of that incident (Viasat's own account of KA-SAT, Symantec's Thrip report, NASA's Inspector General…), so the mapping is SPARTA's analysts', not ours. Hover a technique for the incidents it's cited for." right={<span style={{ display: "flex", gap: 8, alignItems: "center", textTransform: "none", letterSpacing: 0, fontWeight: 500 }}>SPARTA's own citations <ConfidenceBadge confidence={data.confidence.sparta} ui={ui} /></span>}>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
            <button onClick={() => setSel(null)} style={pill(!sel)}>All incidents</button>
            {data.incidents.filter(i => i.sparta.length).map(i => <button key={i.id} onClick={() => setSel(i.id)} style={pill(sel === i.id)}>{i.name}</button>)}
          </div>
          <SpartaCoverage matrix={data.matrix} sel={sel} incidents={data.incidents} ui={ui} />
        </Box>
      </div>

      <div data-box="The incidents" style={{ scrollMarginTop: 12 }}>
        <div style={{ ...sectionLabel(ui), margin: "4px 0 10px" }}>The incidents, newest first</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {data.incidents.map(inc => <IncidentCard key={inc.id} inc={inc} ui={ui} on={sel === inc.id} onPick={pick} />)}
        </div>
        <div style={{ fontSize: fs(11), color: B.textDim, marginTop: 10, lineHeight: 1.5, maxWidth: "110ch" }}>Jamming and spoofing of satellite signals (GPS around the Baltic, Starlink over Ukraine) are left out: they're electronic warfare, reported daily around current wars. SPARTA covers them as techniques under Execution.</div>
      </div>

      <Box title="Who goes after space systems" jump="Who goes after them" ui={ui} about={<>From ATT&CK, by keyword rules: a sentence with a targeting verb and then satellites, the space sector or geospatial imaging, or one that says satellite internet carried command and control. Groups that target "aerospace" are on <a href="#/kb/defense-industry" style={{ color: B.accent }}>Defense Industry</a>, where the word means aircraft as much as spacecraft.</>} right={note(ui, "ATT&CK's descriptions, and the incidents")}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 620 }}>
            {head("Group or software", "Why it's here")}
            <tbody>
              {data.actors.map(a => (
                <tr key={a.id}>
                  <td style={{ ...cell, minWidth: 160 }}>
                    <a href={entityPath(a)} style={{ display: "inline-flex", gap: 6, alignItems: "center", color: B.text, fontWeight: 700, textDecoration: "none" }}><TypeIcon type={a.type} color={getConf(a.type).color} size={13} />{a.name}</a>
                    <div style={{ marginTop: 3 }}><IdBadge id={a.attackId} ui={ui} /></div>
                  </td>
                  <td style={cell}>
                    {a.why.map((w, i) => (
                      <div key={i} style={{ marginBottom: i < a.why.length - 1 ? 8 : 0 }}>
                        <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", marginBottom: 3 }}>
                          {w.kind === "incident" ? <a href={incidentPath(w.incident)} onClick={() => setSel(w.incident)} style={{ textDecoration: "none" }}><Chip ui={ui} color={B.primary}>{w.label}</Chip></a> : <Chip ui={ui}>{w.label}</Chip>}
                          <ConfidenceBadge confidence={w.confidence} ui={ui} />
                        </div>
                        <div style={{ color: B.textMuted, lineHeight: 1.5 }}>{w.kind === "incident" ? w.sentence : `“${w.sentence}”`}</div>
                      </div>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Box>

      <div id="space-cves" style={{ scrollMarginTop: 12 }}>
        <Box title={`Vulnerabilities in satellite and space products (${nf(cv.total)})`} jump="All vulnerabilities" ui={ui} about="CVEs whose descriptions mention satellite terminals and modems (VSAT, SATCOM, Iridium, Inmarsat, Hughes, iDirect…), ground stations and mission software (CCSDS, telecommands, spacecraft), GNSS receivers, or satellite TV receivers. Products that are only called “Satellite” (Red Hat Satellite, NETGEAR's Orbi satellites) are left out." right={note(ui, cv.state.builtAt ? `NVD keyword searches, read ${ago(cv.state.builtAt)}` : "NVD keyword searches")}>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
            <button onClick={() => setSegment("")} style={pill(!segment)}>All {nf(cv.total)}</button>
            {cv.bySegment.filter(s => s.count).map(s => <button key={s.label} onClick={() => setSegment(s.label)} style={pill(segment === s.label)}>{s.label} {nf(s.count)}</button>)}
          </div>
          {cveRows.length ? <>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 680 }}>
                {head("CVE", "Product", "What", "Severity", "Published")}
                <tbody>
                  {(allCves ? cveRows : cveRows.slice(0, CVES_SHOWN)).map(c => (
                    <tr key={c.id}>
                      <td style={{ ...cell, whiteSpace: "nowrap" }}><a href={`#/kb/e/${c.id}`} style={{ color: B.text, fontWeight: 700, textDecoration: "none" }}>{c.id}</a> <KevBadge kev={c.kev} ui={ui} /></td>
                      <td style={{ ...cell, color: B.textMuted, minWidth: 120 }}>{c.vendor ? `${c.vendor} ${c.product || ""}` : "—"}</td>
                      <td style={{ ...cell, color: B.textMuted, lineHeight: 1.5 }}>{c.title}</td>
                      <td style={{ ...cell, whiteSpace: "nowrap", color: B.textMuted }}>{c.score != null ? `${c.score} ${c.severity || ""}` : "—"}</td>
                      <td style={{ ...cell, whiteSpace: "nowrap", color: B.textMuted }}>{c.published ? fmtDate(c.published) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {cveRows.length > CVES_SHOWN && <button onClick={() => setAllCves(v => !v)} style={{ ...pill(false), marginTop: 10 }}>{allCves ? "Show fewer" : `Show all ${nf(cveRows.length)}`}</button>}
          </> : <div style={{ fontSize: fs(12), color: B.textDim }}>{cv.state.builtAt ? "None." : cv.state.error ? `Unavailable (${cv.state.error})` : "Loading… these arrive after the day's CVE download."}</div>}
        </Box>
      </div>

      <div style={grid(420)}>
        <Box title="North Korea's satellite launches" jump="Satellite launches" ui={ui} about="Launches of rockets meant to put satellites in orbit, from the CNS North Korea Missile Test Database (to November 2024). UN Security Council resolutions bar North Korea from any launch using ballistic missile technology, satellite launches included. The Chollima-1 put the Malligyong-1 reconnaissance satellite in orbit in November 2023." right={more(ui, "#/kb/missiles?class=slv", "On the missile map")}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 420 }}>
              {head("Date", "Rocket", "Result", "Came down")}
              <tbody>
                {data.launches.map(l => (
                  <tr key={l.n}>
                    <td style={{ ...cell, whiteSpace: "nowrap", color: B.textMuted }}>{fmtDate(l.date)}</td>
                    <td style={{ ...cell, fontWeight: 600, color: B.text }}>{l.missile || "Not named"}<div style={{ fontSize: fs(11), fontWeight: 400, color: B.textDim }}>{l.site}</div></td>
                    <td style={{ ...cell, color: l.outcome === "Success" ? B.text : B.textMuted }}>{l.outcome}</td>
                    <td style={{ ...cell, color: B.textMuted }}>{l.landing || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Box>
        <Box title="Ransomware claims against space companies" jump="Ransomware claims" ui={ui} right={note(ui, `${nf(rw.total)} claims, ${nf(rw.last365)} in 12 months; the criminals' own`)}
          about="Picked out by the words in each victim's company description: satellites, satellite communications, spacecraft, launch vehicles, the space industry, a space agency. They're leak-site claims, not confirmed breaches.">
          {rw.recent.length ? (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 460 }}>
                {head("Company (claimed)", "Group", "Matched", "Claimed")}
                <tbody>
                  {rw.recent.map(r => (
                    <tr key={r.vid}>
                      <td style={{ ...cell, fontWeight: 600 }}><a href={victimPath(r.vid)} style={{ color: B.text, textDecoration: "none" }}>{r.title}</a>{r.countryName && <div style={{ fontSize: fs(11), fontWeight: 400, color: B.textDim }}>{r.countryName}</div>}</td>
                      <td style={cell}>{r.group.link ? <a href={`#/kb/e/${encodeURIComponent(r.group.link)}`} style={{ color: B.textMuted }}>{r.group.label}</a> : r.group.label}</td>
                      <td style={{ ...cell, color: B.textMuted }}>{r.term}</td>
                      <td style={{ ...cell, color: B.textMuted, whiteSpace: "nowrap" }}>{r.day ? fmtDate(r.day) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <div style={{ fontSize: fs(12), color: B.textDim }}>{rw.builtAt ? "No claims matched." : "Loading… victims arrive a minute or two after the server starts."}</div>}
        </Box>
      </div>

      <Box title={`Reports on the space sector (${nf(data.reports.total)})`} jump="Reports" ui={ui} right={note(ui, "by title, summary and tags")}>
        {data.reports.items.length
          ? <div style={grid(300)}>{data.reports.items.map(s => <ReportCard key={s.id} s={s} ui={ui} />)}</div>
          : <div style={{ fontSize: fs(12), color: B.textDim }}>None among the current reports.</div>}
      </Box>
    </div>
  );
}
